import React, { useEffect, useState } from 'react';

import * as JKYModel from '../custom-types';

// Pieces the TV, the phones and the control panel all use for trivia night.

export const ANSWER_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export const DIFFICULTY_LABELS: { [key: string]: string } = {
  any: 'Mixed difficulty',
  easy: 'Easy',
  medium: 'Medium',
  hard: 'Hard',
};

export const roundLabel = (round: JKYModel.TriviaRoundInfo | undefined) =>
  round ? `${round.category} · ${DIFFICULTY_LABELS[round.difficulty] || 'Mixed difficulty'}` : '';

/** Seconds left until the server's end time, ticking, and the share of the phase left. */
export const useCountdown = (trivia: JKYModel.TriviaState | undefined, totalSeconds: number) => {
  const endsAt = trivia && trivia.endsAt;
  const offset = (trivia && trivia.clockOffset) || 0;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!endsAt) {
      return;
    }
    const timer = window.setInterval(() => setNow(Date.now()), 200);
    return () => window.clearInterval(timer);
  }, [endsAt]);

  if (!endsAt) {
    return { seconds: 0, share: 0 };
  }
  const leftMs = Math.max(0, endsAt - (now + offset));
  return {
    seconds: Math.ceil(leftMs / 1000),
    share: totalSeconds > 0 ? Math.min(1, leftMs / (totalSeconds * 1000)) : 0,
  };
};

interface TriviaAvatarProps {
  person: { username: string; profilePicUrl?: string | null };
  className?: string;
}

/** Their profile picture, or their initials where they have none. */
export const TriviaAvatar = ({ person, className }: TriviaAvatarProps) => {
  const classes = ['trivia-avatar', className].filter(Boolean).join(' ');
  if (person.profilePicUrl) {
    return <img className={classes} src={person.profilePicUrl} alt="" />;
  }
  const initials = (person.username || '?')
    .replace(/[^a-z0-9]/gi, '')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span className={classes} aria-hidden="true">
      {initials}
    </span>
  );
};

export const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

export const formatPoints = (n: number) => n.toLocaleString('en-US');

interface TeamFacesProps {
  members: { id: number; username: string; profilePicUrl?: string | null }[];
  max: number;
  className?: string;
  avatarClassName?: string;
}

/** A team's pictures, overlapping, with a count for anyone past the first few. */
export const TeamFaces = ({ members, max, className, avatarClassName }: TeamFacesProps) => (
  <span className={['trivia-faces', className].filter(Boolean).join(' ')}>
    {members.slice(0, max).map((m) => (
      <TriviaAvatar key={m.id} person={m} className={avatarClassName} />
    ))}
    {members.length > max && <span className="trivia-faces-more">+{members.length - max}</span>}
  </span>
);

interface GlobalRankProps {
  person: { wins?: number; globalRank?: number | null };
  /** Also say how many wins, for the places with room for it. */
  showWins?: boolean;
  className?: string;
}

/** Their rank among every trivia player on every jukebox, by wins. Nothing until they win one. */
export const GlobalRank = ({ person, showWins, className }: GlobalRankProps) => {
  if (!person.globalRank) {
    return null;
  }
  const wins = person.wins || 0;
  return (
    <span
      className={['trivia-global-rank', className].filter(Boolean).join(' ')}
      title={`Global trivia rank ${person.globalRank}, ${wins} ${wins === 1 ? 'win' : 'wins'}`}
    >
      #{person.globalRank}
      {showWins && <span className="trivia-global-wins"> · {wins === 1 ? '1 win' : `${wins} wins`}</span>}
    </span>
  );
};

/**
 * Best first by this score, each with a place, ties sharing it (1, 2, 2, 4). For the
 * rankings between rounds, which rank the same people by the game, the round or the day.
 */
export const rankBy = <T,>(items: T[], score: (item: T) => number) => {
  const sorted = items.slice().sort((a, b) => score(b) - score(a));
  let rank = 0;
  let last = -1;
  return sorted.map((item, index) => {
    if (score(item) !== last) {
      rank = index + 1;
      last = score(item);
    }
    return { item, rank };
  });
};
